import { useEffect, useRef } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { apiRequest } from '@/lib/supabase'
import { useToast } from '@/hooks/useToast'

interface Connection {
  id: string
  ig_username: string | null
  is_active: boolean
  consecutive_failures: number
  last_error: string | null
  last_error_at: string | null
  token_expires_at: string | null
  created_at: string
  updated_at: string
}

/**
 * Returns a human-readable relative time string (e.g. "2 hours ago").
 * Falls back to the raw ISO string if the date is invalid.
 */
function timeAgo(isoString: string): string {
  const date = new Date(isoString)
  if (isNaN(date.getTime())) return isoString

  const seconds = Math.floor((Date.now() - date.getTime()) / 1000)

  if (seconds < 60) return 'just now'
  const minutes = Math.floor(seconds / 60)
  if (minutes < 60) return `${minutes} minute${minutes === 1 ? '' : 's'} ago`
  const hours = Math.floor(minutes / 60)
  if (hours < 24) return `${hours} hour${hours === 1 ? '' : 's'} ago`
  const days = Math.floor(hours / 24)
  if (days < 30) return `${days} day${days === 1 ? '' : 's'} ago`
  const months = Math.floor(days / 30)
  if (months < 12) return `${months} month${months === 1 ? '' : 's'} ago`
  const years = Math.floor(months / 12)
  return `${years} year${years === 1 ? '' : 's'} ago`
}

interface Props {
  isConfigured: boolean
}

export function ConnectInstagram({ isConfigured }: Props) {
  const queryClient = useQueryClient()
  const popupRef = useRef<Window | null>(null)
  const { showToast } = useToast()

  const { data, isLoading } = useQuery<{ success: boolean; data: Connection[] }>({
    queryKey: ['ig-connections'],
    queryFn: () => apiRequest('/api/instagram/auth/connections'),
    enabled: isConfigured,
  })

  // Listen for success/error messages from OAuth popup
  useEffect(() => {
    const handler = (event: MessageEvent) => {
      if (event.data?.type === 'instagram-connected') {
        popupRef.current?.close()
        popupRef.current = null
        queryClient.invalidateQueries({ queryKey: ['ig-connections'] })
        const username = event.data.username ? `@${event.data.username}` : 'Instagram account'
        showToast(`${username} connected successfully!`, 'success')
      } else if (event.data?.type === 'instagram-error') {
        popupRef.current?.close()
        popupRef.current = null
        showToast(event.data.message || 'Something went wrong. Please try again.', 'error')
      }
    }
    window.addEventListener('message', handler)
    return () => window.removeEventListener('message', handler)
  }, [queryClient, showToast])

  const getAuthUrlMutation = useMutation({
    mutationFn: () => {
      // Open blank popup immediately — must be in user gesture context to avoid popup blockers
      popupRef.current = window.open('', '_blank', 'width=620,height=720,left=200,top=100')
      return apiRequest<{ url: string }>('/api/instagram/auth/url')
    },
    onSuccess: (data) => {
      if (popupRef.current) {
        popupRef.current.location.href = data.url
      } else {
        window.open(data.url, '_blank')
      }
    },
    onError: () => {
      popupRef.current?.close()
      popupRef.current = null
      showToast('Failed to start OAuth flow. Please try again.', 'error')
    },
  })

  const disconnectMutation = useMutation({
    mutationFn: (id: string) => apiRequest(`/api/instagram/auth/connections/${id}`, 'DELETE'),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['ig-connections'] })
      showToast('Instagram account disconnected.', 'success')
    },
    onError: () => {
      showToast('Failed to disconnect. Please try again.', 'error')
    },
  })

  const connections = data?.data || []

  if (!isConfigured) {
    return (
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300">Connected Accounts</h3>
        </div>
        <div className="p-5 bg-amber-50/50 dark:bg-amber-900/10 border border-amber-200 dark:border-amber-800/40 rounded-xl">
          <div className="flex gap-3">
            <span className="material-symbols-outlined text-amber-500 text-xl mt-0.5">settings</span>
            <div>
              <p className="text-sm font-medium text-amber-800 dark:text-amber-300">
                Instagram integration not set up yet
              </p>
              <p className="text-xs text-amber-600 dark:text-amber-400/70 mt-1 leading-relaxed">
                To connect Instagram accounts, a Meta Developer App needs to be configured with the required API credentials. Contact the site administrator to complete the setup.
              </p>
            </div>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300">Connected Accounts</h3>
        <button
          onClick={() => getAuthUrlMutation.mutate()}
          disabled={getAuthUrlMutation.isPending}
          className="px-3 py-1.5 text-xs font-medium text-white bg-gradient-to-r from-purple-500 to-pink-500 rounded-lg hover:opacity-90 flex items-center gap-1.5 disabled:opacity-50"
        >
          <span className="material-symbols-outlined text-[16px]">add</span>
          {getAuthUrlMutation.isPending ? 'Opening...' : 'Connect Instagram'}
        </button>
      </div>

      {isLoading ? (
        <div className="space-y-3">
          {[1, 2].map(i => (
            <div key={i} className="h-16 bg-gray-100 dark:bg-white/5 rounded-lg animate-pulse" />
          ))}
        </div>
      ) : connections.length === 0 ? (
        <div className="text-center py-8 bg-gray-50 dark:bg-[#0d121b] rounded-lg">
          <span className="material-symbols-outlined text-4xl text-gray-300 dark:text-gray-600 block mb-2">photo_camera</span>
          <p className="text-sm text-gray-500 dark:text-gray-400">No Instagram accounts connected</p>
          <p className="text-xs text-gray-400 mt-1">Connect your Instagram Business account to get started</p>
        </div>
      ) : (
        <div className="space-y-3">
          {connections.map(conn => {
            const isExpired = conn.token_expires_at && new Date(conn.token_expires_at) < new Date()
            const hasErrors = conn.consecutive_failures > 0

            return (
              <div
                key={conn.id}
                className="flex items-center justify-between p-4 bg-white dark:bg-[#1c2230] border border-gray-200 dark:border-white/10 rounded-lg"
              >
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-gradient-to-tr from-purple-500 to-pink-500 flex items-center justify-center text-white font-bold text-sm">
                    {(conn.ig_username || 'IG').charAt(0).toUpperCase()}
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-900 dark:text-white">
                      @{conn.ig_username || 'Unknown'}
                    </p>
                    <div className="flex items-center gap-2 mt-0.5">
                      {conn.is_active && !isExpired ? (
                        <span className="text-[10px] px-1.5 py-0.5 bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400 rounded-full">Connected</span>
                      ) : isExpired ? (
                        <span className="text-[10px] px-1.5 py-0.5 bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400 rounded-full">Token Expired</span>
                      ) : (
                        <span className="text-[10px] px-1.5 py-0.5 bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400 rounded-full">Disabled</span>
                      )}
                      {hasErrors && (
                        <span className="text-[10px] text-red-500" title={conn.last_error || ''}>
                          {conn.consecutive_failures} failures
                        </span>
                      )}
                    </div>
                    {conn.updated_at && (
                      <p className="text-[10px] text-gray-400 mt-0.5" title={conn.updated_at}>
                        Last activity: {timeAgo(conn.updated_at)}
                      </p>
                    )}
                  </div>
                </div>
                <button
                  onClick={() => { if (confirm('Disconnect this Instagram account? All rules will be paused.')) disconnectMutation.mutate(conn.id) }}
                  disabled={disconnectMutation.isPending}
                  className="px-3 py-1.5 text-xs text-red-600 border border-red-300 dark:border-red-800 rounded-lg hover:bg-red-50 dark:hover:bg-red-900/20 disabled:opacity-50"
                >
                  Disconnect
                </button>
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}
